import clsx from "clsx";
import React, { forwardRef, type Ref, useRef } from "react";
import {
  Modal as HeadlessModal,
  Dialog as HeadlessDialog,
  ModalOverlay as HeadlessModalOverlay,
} from "react-aria-components";
import { CSSTransition } from "react-transition-group";

import styles from "./styles.module.css";
import type { SheetProps } from "./types";

export function _Sheet(props: SheetProps, ref: Ref<HTMLDivElement>) {
  // ADDITIVE EXTENSION (2026-07-06, WDS_SHEET_WIDGET build): size,
  // isDismissable, isKeyboardDismissDisabled, aria-label, aria-labelledby —
  // all default to the pre-extension behaviour so untouched consumers are
  // unaffected. (Destructure kept sorted per sort-destructure-keys.)
  const {
    "aria-label": ariaLabel = "Sheet",
    "aria-labelledby": ariaLabelledby,
    children,
    className,
    isDismissable = true,
    isKeyboardDismissDisabled = false,
    isOpen,
    onEnter,
    onEntered,
    onExit,
    onExited,
    onOpenChange,
    position = "start",
    size,
    ...rest
  } = props;
  const root = document.body.querySelector(
    "[data-theme-provider]",
  ) as HTMLButtonElement;

  const overlayRef = useRef<HTMLDivElement>();

  return (
    <CSSTransition
      in={isOpen}
      nodeRef={overlayRef}
      onEnter={onEnter}
      onEntered={onEntered}
      onExit={onExit}
      onExited={onExited}
      timeout={300}
      unmountOnExit
    >
      <HeadlessModalOverlay
        UNSTABLE_portalContainer={root}
        className={clsx(styles.overlay, className)}
        isDismissable={isDismissable}
        isKeyboardDismissDisabled={isKeyboardDismissDisabled}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        // @ts-expect-error TS is unable to infer the correct type for the render prop
        ref={overlayRef}
      >
        <HeadlessModal
          className={clsx(styles.sheet, styles[position])}
          // `data-size` is only emitted when `size` is provided; without it
          // the base .sheet sizing (80% / max-90%) is preserved.
          data-size={size}
          ref={ref}
          {...rest}
        >
          <HeadlessDialog
            aria-label={ariaLabelledby != null ? undefined : ariaLabel}
            aria-labelledby={ariaLabelledby}
            className={styles.dialog}
          >
            {children}
          </HeadlessDialog>
        </HeadlessModal>
      </HeadlessModalOverlay>
    </CSSTransition>
  );
}

export const Sheet = forwardRef(_Sheet);
